// Post-processor to aggregate weekly selected days across all recurring, non-deleted events
// Adds a single consolidated row per revision group when the union of days changes

import { toChangeTypeFromValues } from 'utils/activity/ActivityTreeBuilder'

const WEEK_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const normalizeDay = d => {
  if (!d) return ''
  const s = String(d).trim()
  if (!s) return ''
  const up = s.toUpperCase()
  const map = {
    MONDAY: 'Monday', TUESDAY: 'Tuesday', WEDNESDAY: 'Wednesday', THURSDAY: 'Thursday', FRIDAY: 'Friday', SATURDAY: 'Saturday', SUNDAY: 'Sunday',
  }
  if (map[up]) return map[up]
  const cap = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
  return WEEK_ORDER.includes(cap) ? cap : cap
}

const computeUnionRecurringDaysAtRev = (threshold, eventsById) => {
  if (!eventsById || typeof eventsById !== 'object') return []
  const out = []
  const seen = new Set()
  const lists = Object.values(eventsById)
  for (const list of lists) {
    if (!Array.isArray(list) || list.length === 0) continue
    let chosen = null
    for (const v of list) {
      const rev = typeof v?.rev === 'number' ? v.rev : Number(v?.rev)
      if (!Number.isFinite(rev)) continue
      if (threshold == null || rev <= threshold) {
        if (!chosen || rev > chosen.rev) chosen = v
      }
    }
    if (!chosen) continue
    const isRecurring = (chosen?.eventType || '') === 'Recurring'
    const isDeleted = !!chosen?.deleted
    if (!isRecurring || isDeleted) continue
    const raw = chosen?.dayOfTheWeek
    let days = []
    if (Array.isArray(raw)) days = raw.filter(Boolean)
    else if (typeof raw === 'string') {
      const s = raw.trim()
      if (s) days = [s]
    }
    for (const d of days) {
      const norm = normalizeDay(d)
      if (norm && !seen.has(norm)) {
        seen.add(norm)
        out.push(norm)
      }
    }
  }
  out.sort((a, b) => WEEK_ORDER.indexOf(a) - WEEK_ORDER.indexOf(b))
  return out
}

export default function recurringDaysUnionBuilder(groups, payload = {}) {
  const { eventsById = {} } = payload
  if (!groups || groups.size === 0) return
  const allRevsAsc = Array.from(groups.keys()).sort((a, b) => Number(a) - Number(b))

  for (let i = 0; i < allRevsAsc.length; i++) {
    const rev = allRevsAsc[i]
    const group = groups.get(rev)
    if (!group) continue

    const prevRev = i > 0 ? allRevsAsc[i - 1] : null
    const prevDays = prevRev == null ? [] : computeUnionRecurringDaysAtRev(prevRev, eventsById)
    const currDays = computeUnionRecurringDaysAtRev(rev, eventsById)
    const oldValue = prevDays.length ? prevDays.join(', ') : 'None'
    const newValue = currDays.length ? currDays.join(', ') : 'None'

    if (oldValue !== newValue) {
      group.children.push({
        field: 'Event days',
        fieldKey: 'event.days',
        oldValue,
        newValue,
        changeType: toChangeTypeFromValues(oldValue === 'None' ? '' : oldValue, newValue === 'None' ? '' : newValue),
        rev,
        ...(group.parentMeta || {}),
      })
    }
  }
}
